// doc-editor Web Component - CodeMirror wrapper with validation highlighting
// NOTE: Does NOT use Shadow DOM because CodeMirror requires access to main document
import template from './doc-editor.html';
import styles from './doc-editor.less';
import { MessageSeverity } from '../types/api-types';
import * as CodeMirror from 'codemirror';
import 'codemirror/lib/codemirror.css';
export class DocEditor extends HTMLElement {
    constructor() {
        super();
        this.editor = null;
        this.validationMarkers = [];
        this.gutterMarkers = [];
        // DO NOT use Shadow DOM - CodeMirror doesn't work inside it
    }
    connectedCallback() {
        this.render();
        // Delay editor initialization to ensure DOM is ready
        setTimeout(() => this.initializeEditor(), 0);
    }
    disconnectedCallback() {
        // CodeMirror cleanup is handled internally
        this.editor = null;
    }
    render() {
        // Inject styles into document head (only once)
        if (!document.getElementById('doc-editor-styles')) {
            const styleEl = document.createElement('style');
            styleEl.id = 'doc-editor-styles';
            styleEl.textContent = styles;
            document.head.appendChild(styleEl);
        }
        // Inject template directly into element (no Shadow DOM)
        this.innerHTML = template;
    }
    initializeEditor() {
        const textarea = this.querySelector('.editor-textarea');
        if (!textarea) {
            console.error('doc-editor: textarea not found');
            return;
        }
        this.editor = CodeMirror.fromTextArea(textarea, {
            lineNumbers: this.getAttribute('line-numbers') !== 'false',
            mode: this.getAttribute('mode') || 'text/plain',
            theme: this.getAttribute('theme') || 'default',
            readOnly: this.getAttribute('readonly') === 'true',
            lineWrapping: true,
            gutters: ['validation-gutter', 'CodeMirror-linenumbers']
        });
        this.editor.setSize('100%', '100%');
        console.log('doc-editor: CodeMirror initialized successfully');
    }
    // Public API
    getValue() {
        return this.editor?.getValue() || '';
    }
    setValue(content) {
        if (this.editor) {
            this.editor.setValue(content);
        }
    }
    scrollToLine(lineNumber) {
        if (this.editor) {
            const lineIndex = lineNumber - 1;
            const t = this.editor.charCoords({ line: lineIndex, ch: 0 }, "local").top;
            const middleHeight = this.editor.getScrollerElement().offsetHeight / 2;
            this.editor.scrollTo(null, t - middleHeight - 5);
            this.editor.setCursor({ line: lineIndex, ch: 0 });
            this.editor.focus();
        }
    }
    highlightMessages(messages) {
        this.clearHighlights();
        if (!this.editor)
            return;
        messages.forEach(msg => {
            if (msg.lineNumber) {
                this.highlightLine(msg);
            }
        });
    }
    clearHighlights() {
        if (!this.editor)
            return;
        // Clear text markers
        this.validationMarkers.forEach(marker => marker.clear());
        this.validationMarkers = [];
        // Clear gutter markers
        this.gutterMarkers.forEach(({ line }) => {
            this.editor.setGutterMarker(line, 'validation-gutter', null);
        });
        this.gutterMarkers = [];
    }
    highlightLine(message) {
        if (!this.editor)
            return;
        const lineIndex = (message.lineNumber || 1) - 1;
        // Highlight the line
        const cssClass = this.getSeverityClass(message.severity);
        const from = { line: lineIndex, ch: message.characterStart || 0 };
        const to = { line: lineIndex, ch: message.characterEnd || this.editor.getLine(lineIndex)?.length || 0 };
        const marker = this.editor.markText(from, to, {
            className: `validation-highlight ${cssClass}`
        });
        this.validationMarkers.push(marker);
        // Add gutter marker
        const gutterMarker = this.createGutterMarker(message);
        this.editor.setGutterMarker(lineIndex, 'validation-gutter', gutterMarker);
        this.gutterMarkers.push({ line: lineIndex, element: gutterMarker });
    }
    createGutterMarker(message) {
        const marker = document.createElement('div');
        marker.className = `validation-marker ${this.getSeverityClass(message.severity)}`;
        marker.title = message.message;
        const icon = this.getIcon(message.severity);
        marker.textContent = icon;
        return marker;
    }
    getSeverityClass(severity) {
        // Handle both enum and string values
        const severityStr = String(severity);
        switch (severityStr) {
            case 'Error':
            case MessageSeverity.Error:
                return 'error';
            case 'Warning':
            case MessageSeverity.Warning:
                return 'warning';
            case 'Info':
            case MessageSeverity.Info:
                return 'info';
            default:
                return '';
        }
    }
    getIcon(severity) {
        // Handle both enum and string values
        const severityStr = String(severity);
        switch (severityStr) {
            case 'Error':
            case MessageSeverity.Error:
                return '?';
            case 'Warning':
            case MessageSeverity.Warning:
                return '??';
            case 'Info':
            case MessageSeverity.Info:
                return '??';
            default:
                return '�';
        }
    }
}
// Register the custom element
customElements.define('doc-editor', DocEditor);
